import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { Button } from '@/components/ui';
import { AlertCircle, Copy, Check, RefreshCw, X } from 'lucide-react';
import { reportarCrashDeRender } from '@/shared/services/client-error-reporter';

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

// Componente funcional para mostrar el error
const ErrorDisplay: React.FC<{
  error: Error;
  errorInfo: React.ErrorInfo | null;
}> = ({ error, errorInfo }) => {
  const [copied, setCopied] = useState(false);

  const getErrorDetails = (): string => {
    const details = [
      `Error: ${error.name || 'Error'}`,
      `Mensaje: ${error.message || 'Error desconocido'}`,
      '',
      'Stack Trace:',
      error.stack || 'No hay stack trace disponible',
    ];

    if (errorInfo?.componentStack) {
      details.push('', 'Component Stack:');
      details.push(errorInfo.componentStack);
    }

    return details.join('\n');
  };

  const handleCopy = async () => {
    try {
      const errorText = getErrorDetails();
      await navigator.clipboard.writeText(errorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Error al copiar
    }
  };

  const errorDetails = getErrorDetails();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl shadow-xl border-red-200">
        <CardHeader className="bg-red-50 border-b border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-red-900">
                Algo salió mal
              </CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Mensaje de error principal */}
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
              <p className="text-red-800 font-semibold text-lg mb-2">
                {error.name || 'Error'}
              </p>
              <p className="text-red-700">
                {error.message || 'Error desconocido'}
              </p>
            </div>

            {/* Caja de detalles del error */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Detalles del Error
                </h3>
                <Button
                  onClick={handleCopy}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 text-green-600" />
                      <span className="text-green-600">¡Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      <span>Copiar error</span>
                    </>
                  )}
                </Button>
              </div>
              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap break-words">
                  {errorDetails}
                </pre>
              </div>
            </div>

            {/* Información adicional */}
            {errorInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Información adicional:</strong> Este error fue
                  capturado por el Error Boundary de React. Si el problema
                  persiste, contacta al equipo de soporte.
                </p>
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button
                onClick={() => window.location.reload()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Recargar página
              </Button>
              <Button
                onClick={() => (window.location.href = '/')}
                variant="outline"
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Volver al inicio
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      error,
      errorInfo,
    });
    // GH#1778 capa 4b — este boundary no reportaba a ningun lado: el cliente veia la
    // pantalla de error y nadie se enteraba. Ahora va a la caja negra propia.
    reportarCrashDeRender({ error, componentStack: errorInfo.componentStack, boundary: 'app' });
  }

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
